import RouteInterceptDialog from "../../components/RouteIntercept/RouteInterceptDialog";
import { useContext, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Icon } from "@livingmap/core-ui-v2";

import { Sheet } from "../../components/Sheet";

import {
  clearLocation,
  storeLocation,
} from "../../redux/slices/applicationSlice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { usePostRouteData } from "../RouteView/hooks/usePostRouteData";
import { useDisplayRoute } from "../../hooks/useDisplayRoute";
import { useRedirectToDesktopRoute } from "../../hooks/useRedirectToDesktopRoute";
import { useRoutingUserActiveFloor } from "../../hooks/useRoutingUserActiveFloor";

import TimeToDestination from "../../components/TimeToDestination/TimeToDestination";
import RouteContent from "../RouteView/components/RouteContent/RouteContent";

import { QueryParams, RoutingPath } from "../../utils/types";
import { push, replace } from "../../utils/navigate";
import { buildRoutingPath } from "../../utils/buildRoutingPath";
import { showRouteTime } from "../../utils/showRouteTime";

import styles from "./JourneyOverviewView.module.scss";
import { MapContext } from "../../contexts/MapContext";
import {
  useGetFeatureByIdQuery,
  useGetMapConfigQuery,
} from "../../redux/services/vectormapsAPI";
import { useTranslation } from "react-i18next";

const WARNING_MESSAGE_HEIGHT = 64;
const MILESTONE_HEIGHT = 73;

export default function JourneyOverviewView() {
  const { floorControl, routingControl } = useContext(MapContext);
  const { t } = useTranslation();

  useRedirectToDesktopRoute();
  useRoutingUserActiveFloor({
    getActiveFloor: () => floorControl?.getActiveFloor(),
  });

  const dispatch = useAppDispatch();
  const { fromId, toId } = useParams();
  const location = useLocation();

  const splitFromId = fromId?.split(",");
  const splitToId = toId?.split(",");

  const { language, savedLocation, routingUserActiveFloor, project } =
    useAppSelector((state) => state.application);

  const { data: mapData } = useGetMapConfigQuery();

  const { data: originByIdData } = useGetFeatureByIdQuery(
    { featureId: fromId!, project },
    {
      skip: !fromId || fromId === "-" || !project || splitFromId?.length === 2,
    },
  );

  const { data: destinationByIdData } = useGetFeatureByIdQuery(
    { featureId: toId!, project },
    { skip: !toId || toId === "-" || !project || splitToId?.length === 2 },
  );

  const {
    data: routeData,
    isError,
    stepFreeRoute,
    routeRequestKey,
  } = usePostRouteData({
    fromId,
    toId,
    floor: routingUserActiveFloor,
  });

  const { routeMilestones } = useDisplayRoute({
    segments: routeData?.segments,
    sequenceOrder: routeData?.routeMetadata[0].sequenceOrder,
    directionsEnabled: mapData?.routing?.directions_enabled,
    originFeature: originByIdData || splitFromId,
    destinationFeature: destinationByIdData || splitToId,
    intercept: routeData?.intercept,
    activeFloor: routingUserActiveFloor,
    stepFreeRoute,
  });

  const showTime =
    routeData &&
    mapData &&
    showRouteTime(
      mapData.routing?.time_to_destination_enabled,
      mapData.routing?.time_to_destination_limit,
      routeData.routeMetadata[0].totalTime,
    );

  useEffect(() => {
    dispatch(storeLocation(location));
  }, [dispatch, location]);

  useEffect(() => {
    if (isError)
      dispatch(
        replace({
          pathOrLocation: "/",
          discardParams: [QueryParams.STEP_FREE, QueryParams.MILESTONE_INDEX],
        }),
      );
  }, [dispatch, isError, savedLocation]);

  return routeData && mapData ? (
    <>
      <RouteInterceptDialog
        intercept={routeData.intercept}
        requestKey={routeRequestKey}
      />
      <TimeToDestination
        skipGo
        dataQA={"journey-time-to-destination"}
        className={styles.timeToDestination}
        currentStep={0}
        kilometres={routeData.routeMetadata[0].totalLength}
        displayUnit={mapData.routing?.display_unit_system || "metric"}
        minutes={routeData.routeMetadata[0].totalTime}
        live={fromId?.includes(",") || toId?.includes(",")}
        steps={routeMilestones.length}
        showTime={showTime}
        onClick={() => {
          dispatch(clearLocation());
          routingControl!.setJourneyMode(false);
          dispatch(
            replace({
              pathOrLocation: "/",
              discardParams: [
                QueryParams.STEP_FREE,
                QueryParams.MILESTONE_INDEX,
              ],
            }),
          );
        }}
      />
      <Sheet
        scrollLocking={false}
        dataQA="journey-overview-view-sheet"
        snapPoints={({ maxHeight, headerHeight }) => [
          headerHeight,
          headerHeight + WARNING_MESSAGE_HEIGHT + MILESTONE_HEIGHT + 20, // 20px margin
          maxHeight - 20,
        ]}
        open
        defaultSnap={({ headerHeight }) => headerHeight}
        header={
          <div className={styles.header}>
            <div className={styles.titleContainer}>
              <Icon
                height={19}
                type="NearMeTopIcon"
                dataQA="near-me-top-icon"
              />
              <p className={styles.title}>{t("journey_overview.heading")}</p>
            </div>
            <Icon
              className={styles.closeButton}
              type="CloseIcon"
              dataQA="close-route-menu-icon"
              onClick={() => {
                dispatch(clearLocation());

                dispatch(
                  push({
                    pathOrLocation: buildRoutingPath(RoutingPath.JOURNEY, {
                      fromId,
                      toId,
                    }),
                    ...(!savedLocation && {
                      discardParams: [
                        QueryParams.STEP_FREE,
                        QueryParams.MILESTONE_INDEX,
                      ],
                    }),
                  }),
                );
              }}
            />
          </div>
        }
      >
        <RouteContent
          intercept={routeData.intercept}
          routeMilestones={routeMilestones}
          totalTime={routeData?.routeMetadata[0].totalTime || 0}
          showTime={showTime}
          language={language}
          displayUnit={mapData?.routing?.display_unit_system || "metric"}
          showStepOption={mapData?.routing?.directions_enabled}
        />
      </Sheet>
    </>
  ) : null;
}
